import { useMemo } from "react";
import { Alert } from "react-bootstrap";
import FastForm from "../../components/FastForm/FastForm";
import useEvents from "../../hooks/useEvents";
import { usePrecargaEvents } from "../../hooks/usePrecargaEvents";
import {
  generateSlug,
  pesosTocentavos,
  validatePrecargaDates,
  validatePrecargaLimits,
} from "../../utils/precargaHelpers";

export default function CreatePrecargaEventForm({ cashlessEventId, onSuccess, onCancel }) {
  const { data: cashlessEvents, isLoading: isLoadingEvents } = useEvents();
  const { createMutation } = usePrecargaEvents();

  const selectedEvent = useMemo(() => {
    if (!cashlessEventId || !cashlessEvents) return null;
    return cashlessEvents.find(
      (e) => parseInt(e.eventid) === parseInt(cashlessEventId)
    );
  }, [cashlessEventId, cashlessEvents]);

  if (isLoadingEvents) {
    return <Alert variant="info">Cargando evento...</Alert>;
  }

  if (!selectedEvent) {
    return <Alert variant="danger">No se encontró el evento cashless</Alert>;
  }

  return (
    <div>
      <FastForm
        mutation={createMutation}
        sendText="Configurar Precarga"
        clearOnSuccess={false}
        columns={[1,2,4]}
        containerStyle={{ maxWidth: "1200px" }}
        fields={[
          {
            type: "divider",
            title: "Evento",
          },
          {
            name: "cashlessEventId",
            type: "hidden",
            defaultValue: parseInt(cashlessEventId),
          },
          {
            name: "slug",
            label: "Slug (URL amigable)",
            type: "text",
            placeholder: selectedEvent
              ? generateSlug(selectedEvent.name)
              : "ejemplo-evento-2026",
            defaultValue: selectedEvent
              ? generateSlug(selectedEvent.name)
              : "",
            attributes: {
              required: true,
              pattern: "[a-z0-9-]+",
            },
            help: "Solo letras minúsculas, números y guiones. Se generará automáticamente del nombre del evento.",
          },
          {
            type: "divider",
            title: "Carga",
          },
          {
            name: "currency",
            label: "Moneda",
            type: "select",
            defaultValue: "ARS",
            options: [
              { value: "ARS", label: "Peso Argentino (ARS)" },
              { value: "BRL", label: "Real Brasileño (BRL)" },
              { value: "CLP", label: "Peso Chileno (CLP)" },
              { value: "COP", label: "Peso Colombiano (COP)" },
              { value: "EUR", label: "Euro (EUR)" },
              { value: "PYG", label: "Guaraní Paraguayo (PYG)" },
              { value: "MXN", label: "Peso Mexicano (MXN)" },
              { value: "USD", label: "Dólar (USD)" },
              { value: "UYU", label: "Peso Uruguayo (UYU)" },
            ],
            attributes: { required: true },
          },
          {
            name: "minTopup",
            label: "Monto Mínimo de Recarga (en pesos)",
            type: "number",
            defaultValue: 10000,
            attributes: {
              required: true,
              min: 0,
              step: 1,
            },
            prefix: "$",
            help: "Monto mínimo que puede recargar un usuario",
          },
          {
            name: "maxTopup",
            label: "Monto Máximo de Recarga (en pesos)",
            type: "number",
            defaultValue: 100000,
            attributes: {
              required: true,
              min: 0,
              step: 1,
            },
            prefix: "$",
            help: "Monto máximo que puede recargar un usuario",
          },
          {
            type: "divider",
            title: "Períodos de Operación",
          },
          {
            name: "preloadOpenAt",
            label: "Apertura de Precargas",
            type: "datetime-local",
            attributes: { required: false },
            help: "Fecha y hora de inicio para realizar precargas",
          },
          {
            name: "preloadCloseAt",
            label: "Cierre de Precargas",
            type: "datetime-local",
            attributes: { required: false },
            help: "Fecha y hora de cierre para realizar precargas",
          },
          {
            name: "refundOpenAt",
            label: "Apertura de Devoluciones",
            type: "datetime-local",
            attributes: { required: false },
            help: "Fecha y hora de inicio para solicitar devoluciones",
          },
          {
            name: "refundCloseAt",
            label: "Cierre de Devoluciones",
            type: "datetime-local",
            attributes: { required: false },
            help: "Fecha y hora de cierre para solicitar devoluciones",
          },
        ]}
        validate={(data) => {
          // Validate dates
          const dateValidation = validatePrecargaDates(data);
          if (dateValidation !== true) {
            return dateValidation;
          }

          // Validate limits
          const limitsValidation = validatePrecargaLimits({
            minTopup: pesosTocentavos(data.minTopup),
            maxTopup: pesosTocentavos(data.maxTopup),
          });
          if (limitsValidation !== true) {
            return limitsValidation;
          }

          return true;
        }}
        parseData={(data) => {
          // Convert pesos to centavos
          const parsedData = {
            ...data,
            cashlessEventId: parseInt(data.cashlessEventId),
            minTopup: pesosTocentavos(data.minTopup),
            maxTopup: pesosTocentavos(data.maxTopup),
          };

          // Convert dates to ISO strings (or null if empty)
          if (data.preloadOpenAt) {
            parsedData.preloadOpenAt = new Date(data.preloadOpenAt).toISOString();
          } else {
            parsedData.preloadOpenAt = null;
          }

          if (data.preloadCloseAt) {
            parsedData.preloadCloseAt = new Date(data.preloadCloseAt).toISOString();
          } else {
            parsedData.preloadCloseAt = null;
          }

          if (data.refundOpenAt) {
            parsedData.refundOpenAt = new Date(data.refundOpenAt).toISOString();
          } else {
            parsedData.refundOpenAt = null;
          }

          if (data.refundCloseAt) {
            parsedData.refundCloseAt = new Date(data.refundCloseAt).toISOString();
          } else {
            parsedData.refundCloseAt = null;
          }

          return parsedData;
        }}
      />

      {onCancel && (
        <button className="btn btn-secondary mt-3" onClick={onCancel}>
          Cancelar
        </button>
      )}
    </div>
  );
}
